import React, {useCallback, useEffect, useMemo} from 'react';
import {Organization} from '../../interfaces';
import {Box, Button, Modal, Typography} from '@mui/material';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import styles from '../../styles';
import FormTextField from '../shared/FormTextField';
import FormCheckbox from '../shared/FormCheckbox';
import {updateOrganization} from '../../api/adminAPI';

type OrganizationUpdateForm = {
  id: number;
  name: string;
  country: string;
  stateOrProvince: string;
  hideLeaderboard: boolean;
};

interface Props {
  organization: Organization;
  loading: boolean;
  onUpdate: () => void;
}

const UpdateOrgModal: React.FC<Props> = ({organization, loading, onUpdate}) => {
  const {t} = useTranslation();
  const [showUpdateDialog, setShowUpdateDialog] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  // only the editable fields: the organization also carries teams / profiles
  // which must not end up in the form state
  const defaultValues = useMemo<OrganizationUpdateForm>(
    () => ({
      id: organization.id ?? 0,
      name: organization.name ?? '',
      country: organization.country ?? '',
      stateOrProvince: organization.stateOrProvince ?? '',
      hideLeaderboard: organization.hideLeaderboard ?? false,
    }),
    [organization],
  );
  const {handleSubmit, reset, control} = useForm<OrganizationUpdateForm>({
    defaultValues,
  });

  // defaultValues are only read on first mount; keep the form in sync after
  // the parent refetches the organization following a save
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleUpdateDialogClick = useCallback(() => {
    setShowUpdateDialog(true);
  }, []);

  const handleAddDialogClose = useCallback(() => {
    reset();
    setPending(false);
    setShowUpdateDialog(false);
  }, [reset]);

  const handleUpdateSubmit = useCallback(
    async (data: OrganizationUpdateForm) => {
      setPending(true);
      await updateOrganization({
        id: data.id,
        name: data.name,
        country: data.country,
        stateOrProvince: data.stateOrProvince,
        hideLeaderboard: data.hideLeaderboard,
      });
      onUpdate();
      handleAddDialogClose();
    },
    [handleAddDialogClose, onUpdate],
  );

  return (
    <>
      <Button
        sx={{mb: 2, mr: 2}}
        onClick={handleUpdateDialogClick}
        disabled={loading}>
        {t('admin.updateOrganization')}
      </Button>
      <Modal
        open={showUpdateDialog}
        onClose={handleAddDialogClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('admin.updateOrganization')}
          </Typography>
          <form onSubmit={handleSubmit(handleUpdateSubmit)} className="form">
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="name"
              label={t('general.name') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="country"
              label={t('admin.country') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="stateOrProvince"
              label={t('admin.stateOrProvince') as string}
              required
            />
            <FormCheckbox
              control={control as unknown as Control<FieldValues>}
              name="hideLeaderboard"
              label={t('admin.hideLeaderboard') as string}
            />
            <Button disabled={pending} type="submit">
              {t('general.submit')}
            </Button>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(UpdateOrgModal);
